import type { ComponentProps } from 'react';
import { Button } from '@savvy-kit/components/button';
import { cn } from '@savvy-kit/lib/utils';
import { ChevronLeftIcon, ChevronRightIcon, MoreHorizontalIcon } from 'lucide-react';

function Pagination({ className, ...props }: ComponentProps<'nav'>) {
  return (
    <nav
      role="navigation"
      aria-label="pagination"
      data-slot="pagination"
      className={cn('mx-auto flex w-full justify-center', className)}
      {...props}
    />
  );
}

function PaginationContent({ className, ...props }: ComponentProps<'ul'>) {
  return <ul data-slot="pagination-content" className={cn('flex flex-row items-center gap-1', className)} {...props} />;
}

function PaginationItem({ ...props }: ComponentProps<'li'>) {
  return <li data-slot="pagination-item" {...props} />;
}

type PaginationButtonProps = {
  isActive?: boolean;
} & ComponentProps<typeof Button>;

function PaginationButton({ isActive, size = 'default', className, ...props }: PaginationButtonProps) {
  return (
    <Button
      aria-current={isActive ? 'page' : undefined}
      data-slot="pagination-link"
      data-active={isActive}
      size={size}
      variant="ghost"
      className={cn(
        'size-9 text-sm font-medium data-[active=true]:border data-[active=true]:border-muted data-[active=true]:shadow-xs',
        className,
      )}
      {...props}
    />
  );
}

function PaginationPrevious({ className, ...props }: ComponentProps<typeof PaginationButton>) {
  return (
    <PaginationButton
      aria-label="Go to previous page"
      size="default"
      className={cn('h-9 w-auto gap-1 px-3 text-sm font-medium', className)}
      icon={<ChevronLeftIcon className="size-4" />}
      iconPosition="left"
      {...props}
    >
      Previous
    </PaginationButton>
  );
}

function PaginationNext({ className, ...props }: ComponentProps<typeof PaginationButton>) {
  return (
    <PaginationButton
      aria-label="Go to next page"
      size="default"
      className={cn('h-9 w-auto gap-1 px-3 text-sm font-medium', className)}
      icon={<ChevronRightIcon className="size-4" />}
      iconPosition="right"
      {...props}
    >
      Next
    </PaginationButton>
  );
}

function PaginationEllipsis({ className, ...props }: ComponentProps<'span'>) {
  return (
    <span
      aria-hidden
      data-slot="pagination-ellipsis"
      className={cn('flex size-9 items-center justify-center text-sm font-medium', className)}
      {...props}
    >
      <MoreHorizontalIcon className="size-4" />
      <span className="sr-only">More pages</span>
    </span>
  );
}

export {
  Pagination as PaginationRoot,
  PaginationContent,
  PaginationButton,
  PaginationItem,
  PaginationPrevious,
  PaginationNext,
  PaginationEllipsis,
};
